import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import chatNotifyBody from '@/function/conversation/chat/chat.notify.body';
import chatNotifyDecide from '@/function/conversation/chat/chat.notify.decide';
import chatUnreadTotal from '@/function/conversation/chat/chat.unread.total';
import { onChatIncoming } from '@/lib/chat/chat.incoming.bus';
import { getChatNotifyPrefs } from '@/lib/chat/chat.notify.prefs';
import { chatDesktopPermission, chatPlaySound, chatShowDesktop, chatShowToast } from '@/lib/chat/chat.notify.output';
import { pushIsActive } from '@/lib/push/push.client';
import { usePushBridge } from '@/hooks/use.push.bridge';
import { selectSessionUser } from '@/store/auth/auth.selectors';
import { useListChatChannelsQuery } from '@/store/api/conversation/chat/chat.api';
import { useAppSelector } from '@/store/store.hooks';

const CHAT_PATH = /^\/conversations\/chat\/([^/]+)/;
const SEEN_MAX = 500;

export function useChatNotifications() {
    const navigate = useNavigate();
    const location = useLocation();
    const me = useAppSelector(selectSessionUser)?.authUserId ?? null;
    const channels = useListChatChannelsQuery(undefined, { skip: !me });
    const activeChannelId = CHAT_PATH.exec(location.pathname)?.[1] ?? null;
    const latest = React.useRef({ data: channels.data, me, activeChannelId });
    latest.current = { data: channels.data, me, activeChannelId };
    const seen = React.useRef(new Set<string>());
    usePushBridge();

    React.useEffect(() => onChatIncoming((message) => {
        const { data, me: self, activeChannelId: active } = latest.current;
        const channel = data?.channels.find((entry) => entry.id === message.channelId) ?? null;
        const prefs = getChatNotifyPrefs();
        const visible = document.visibilityState === 'visible' && document.hasFocus();
        const decision = chatNotifyDecide({
            message, channel, me: self, prefs, activeChannelId: active, visible,
            desktopAllowed: chatDesktopPermission() === 'granted' && !pushIsActive(), seen: seen.current.has(message.id),
        });
        if (!message.pending) {
            seen.current.add(message.id);
            if (seen.current.size > SEEN_MAX) seen.current = new Set([...seen.current].slice(-SEEN_MAX / 2));
        }
        if (!channel || (!decision.toast && !decision.desktop)) return;
        const { title, body } = chatNotifyBody(message, channel, data?.members ?? [], self, prefs.preview);
        const open = () => navigate(`/conversations/chat/${encodeURIComponent(channel.id)}`);
        if (decision.toast) chatShowToast(channel.id, title, body, open);
        if (decision.desktop) chatShowDesktop(channel.id, title, body, open);
        if (decision.sound) chatPlaySound();
    }), [navigate]);

    const unread = React.useMemo(() => chatUnreadTotal((channels.data?.channels ?? []).filter((channel) => channel.id !== activeChannelId)), [channels.data, activeChannelId]);
    return unread;
}

export default useChatNotifications;
